import type { DatabaseEngineId } from '@/core/engines';

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { DatabaseObjectTreeNode, DatabaseObjectNodeMetadata } from '../../explorer/models/DatabaseTreeModels';
import { toast } from '../../../components/ui/toaster';
import { getConnectionById, getEngineForConnection } from '../../connections/utils/connectionLookup';
import { openReferencesTab } from '../../navigational-features/find-references/openReferencesTab';
import { buildOeRelationIdentifier } from '../../navigational-features/oeIdentifier';
import {
  executeAction,
  type ObjectActionContext,
  type SchemaObjectType,
} from '../../shared/object-actions';
import { parseNodeId } from '../../explorer/utils/nodeIdUtils';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { findExistingEditorTab } from '../../workspace/pl-sql-debugger';
import { createDefaultPlSqlEditorContent } from '../../workspace/plsql-editor/types';
import { isBetaFeatureEnabled } from '../../../core/beta';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

/**
 * Routine-specific context menu action types
 */
type RoutineContextMenuActionType =
  | 'refresh'
  | 'execute-routine'
  | 'open-definition'
  | 'script-routine-as'
  | 'script-routine-create'
  | 'script-routine-alter'
  | 'script-routine-drop'
  | 'show-dependencies'
  | 'find-references';

const RoutineActions = {
  REFRESH: 'refresh' as RoutineContextMenuActionType,
  EXECUTE: 'execute-routine' as RoutineContextMenuActionType,
  OPEN_DEFINITION: 'open-definition' as RoutineContextMenuActionType,
  SCRIPT_AS: 'script-routine-as' as RoutineContextMenuActionType,
  SCRIPT_CREATE: 'script-routine-create' as RoutineContextMenuActionType,
  SCRIPT_ALTER: 'script-routine-alter' as RoutineContextMenuActionType,
  SCRIPT_DROP: 'script-routine-drop' as RoutineContextMenuActionType,
  SHOW_DEPENDENCIES: 'show-dependencies' as RoutineContextMenuActionType,
  FIND_REFERENCES: 'find-references' as RoutineContextMenuActionType,
};

/**
 * Mapping from Object Explorer action IDs to shared action IDs
 */
const actionIdMapping: Record<string, string> = {
  [RoutineActions.EXECUTE]: 'execute',
  [RoutineActions.SCRIPT_CREATE]: 'script-create',
  [RoutineActions.SCRIPT_ALTER]: 'script-alter',
  [RoutineActions.SCRIPT_DROP]: 'script-drop',
  [RoutineActions.SHOW_DEPENDENCIES]: 'view-dependencies',
};

/**
 * Generate routine-specific context menu actions
 */
export function generateRoutineContextMenuActions(routineType: 'stored-procedure' | 'function', engine?: string): ContextMenuAction[] {
  const routineLabel = routineType === 'stored-procedure' ? 'stored procedure' : 'function';
  const copyLabel = routineType === 'stored-procedure' ? 'procedure' : 'function';

  // Kusto functions have no CREATE/ALTER/DROP scripting, no `Execute…` dialog,
  // and no dependency graph — every routine action routes through the shared
  // script provider / executor, which has no kusto rendering. Offer only the
  // two that work: copy the name and refresh.
  if (engine === 'kusto') {
    return [
      { id: 'copy-name', label: `Copy ${copyLabel} name`, icon: 'clipboard' },
      { id: RoutineActions.REFRESH, label: 'Refresh', icon: 'refresh' },
    ];
  }

  const actions: ContextMenuAction[] = [
    { id: 'copy-name', label: `Copy ${copyLabel} name`, icon: 'clipboard' },
    { id: RoutineActions.EXECUTE, label: 'Execute…', icon: 'play' },
    { id: RoutineActions.OPEN_DEFINITION, label: 'Open definition', icon: 'code' },
    {
      id: RoutineActions.SCRIPT_AS,
      label: `Script ${routineLabel} as`,
      icon: 'code',
      submenu: [
        { id: RoutineActions.SCRIPT_CREATE, label: 'CREATE', icon: 'file-plus' },
        { id: RoutineActions.SCRIPT_ALTER, label: 'ALTER', icon: 'edit' },
        { id: RoutineActions.SCRIPT_DROP, label: 'DROP', icon: 'trash' },
      ],
    },
    { id: RoutineActions.SHOW_DEPENDENCIES, label: 'Show Dependencies', icon: 'git-branch' },
    { id: RoutineActions.FIND_REFERENCES, label: 'Find references…', icon: 'search' },
  ];

  if (engine === 'oracle') {
    actions.push({ id: 'open-in-editor', label: 'Open', icon: 'code' });
    if (isBetaFeatureEnabled('pl-sql-debugger')) {
      actions.push({ id: 'compile-for-debug', label: 'Compile for Debug', icon: 'bug' });
    }
  }

  actions.push(
    { id: 'separator', label: '', isSeparator: true },
    { id: RoutineActions.REFRESH, label: 'Refresh', icon: 'refresh' },
  );

  return actions;
}

/**
 * Extract database name from parent folder node ID
 * Parent ID format: "stored-procedures-folder<->connectionId<->database" or "functions-folder<->connectionId<->database"
 */
function extractDatabaseFromParentId(parentId: string | null | undefined): string | undefined {
  if (!parentId) return undefined;
  const parts = parseNodeId(parentId);
  // Format: [folderType, connectionId, database]
  if (parts.length >= 3) {
    return parts[2];
  }
  return undefined;
}

/**
 * Build ObjectActionContext from a DatabaseObjectTreeNode for routines
 */
function buildActionContext(node: DatabaseObjectTreeNode): ObjectActionContext | null {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: objectName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  // Map tree node type to shared object type
  const objectType: SchemaObjectType =
    objectDetails.type === 'stored-procedure' ? 'procedure' : 'function';

  const { activeConnection, engine } = getConnectionById(connectionId);

  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return null;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
  const resolvedSchema = schema ?? defaultSchema;
  // Prefer database from parent ID, then metadata, then connection default
  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';

  // Extract package info for Oracle package members
  const packageName = objectDetails.metadata?.packageName as string | undefined;
  const overloadIndex = objectDetails.metadata?.overloadIndex as number | undefined;

  return {
    connectionId,
    databaseName: dbName,
    schemaName: resolvedSchema,
    objectName,
    objectType,
    objectId: objectDetails.id,
    packageName,
    overloadIndex,
  };
}

/**
 * Execute a routine context menu action
 *
 * Delegates to the shared object actions system where possible,
 * and handles routine-explorer-specific actions locally.
 */
export function executeRoutineContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: { onRefresh?: (nodeId: string) => void } = {},
  node?: DatabaseObjectTreeNode,
): void {
  // Handle refresh action
  if (actionId === RoutineActions.REFRESH) {
    handlers.onRefresh?.(nodeId);
    return;
  }

  if (!node) {
    toast.error({ title: 'Error', description: 'Routine node not found' });
    return;
  }

  // Check if this action maps to a shared action
  const sharedActionId = actionIdMapping[actionId];
  if (sharedActionId) {
    const context = buildActionContext(node);
    if (context) {
      void executeAction(sharedActionId, context);
    }
    return;
  }

  // Handle routine-specific actions
  switch (actionId) {
    case RoutineActions.OPEN_DEFINITION:
      // Open definition is the same as script-create
      {
        const context = buildActionContext(node);
        if (context) {
          void executeAction('script-create', context);
        }
      }
      break;

    case RoutineActions.FIND_REFERENCES: {
      const context = buildActionContext(node);
      if (!context) break;
      const engineId = getEngineForConnection(context.connectionId) as DatabaseEngineId;
      openReferencesTab({
        connectionId: context.connectionId,
        database: context.databaseName,
        engineId,
        // Engine-aware fold, the same builder the table / view Find-References
        // menus use. The blanket `.toLowerCase()` this replaces produced
        // `get_user_balance` for an Oracle catalog's `GET_USER_BALANCE` while
        // the index records the body's ref folded UPPER — and since the
        // column / routine lookup is narrowed with EXACT folded equality on
        // pg / Oracle (`relationIdentity.ts`'s `catalogLeafRefMatches`), every
        // recorded ref was dropped: Oracle "Find References" on a routine
        // answered "no references" for a routine the index held. Same shape as
        // the CK-B P1-2 fix already applied to the relation menus and to
        // `go-to/routineAtCursor.ts`.
        identifier: buildOeRelationIdentifier(engineId, {
          name: context.objectName,
          schema: context.schemaName,
          database: context.databaseName,
        }),
        identifierKind: 'routine',
        trigger: 'context-menu',
      });
      break;
    }

    case 'open-in-editor':
      {
        const metadata = node.metadata as DatabaseObjectNodeMetadata;
        const { objectDetails } = metadata;
        const ctx = buildActionContext(node);
        if (!ctx) break;

        // Package members: open the parent package editor
        const packageName = objectDetails.metadata?.packageName as string | undefined;
        const editorObjectName = packageName ?? objectDetails.name;
        const editorObjectType = packageName ? 'package' : (objectDetails.type === 'stored-procedure' ? 'procedure' : 'function');

        const { connection: editorConn, activeConnection: editorActive } = getConnectionById(objectDetails.connectionId);
        const { addTab, tabs, setActiveTab, updateTabContent } = useWorkspaceStore.getState();
        const existingId = findExistingEditorTab(tabs, ctx.schemaName, editorObjectName, objectDetails.connectionId);
        if (existingId) {
          setActiveTab(existingId);
          // Set targetMember on existing tab if this is a package member
          if (packageName) {
            updateTabContent(existingId, {
              targetMember: {
                name: objectDetails.name,
                type: objectDetails.type === 'stored-procedure' ? 'procedure' : 'function',
                overloadIndex: ctx.overloadIndex,
              },
            });
          }
          break;
        }
        const editorContent = createDefaultPlSqlEditorContent(
          editorObjectName,
          ctx.schemaName,
          ctx.databaseName,
          editorObjectType as 'procedure' | 'function' | 'package',
        );
        if (packageName) {
          editorContent.targetMember = {
            name: objectDetails.name,
            type: objectDetails.type === 'stored-procedure' ? 'procedure' : 'function',
            overloadIndex: ctx.overloadIndex,
          };
        }
        addTab({
          title: `${ctx.schemaName}.${editorObjectName}`,
          type: 'plsql-editor',
          content: editorContent,
          connectionInfo: {
            connectionId: objectDetails.connectionId,
            connectionName: safeConnectionAlias(editorConn, 'Unnamed Connection'),
            serverHost: safeConnectionServer(editorActive?.connection),
            database: ctx.databaseName,
          },
        });
      }
      break;

    case 'compile-for-debug':
      {
        const metadata = node.metadata as DatabaseObjectNodeMetadata;
        const { objectDetails } = metadata;
        // Package members: compile the parent package, not the individual member
        const packageName = objectDetails.metadata?.packageName as string | undefined;
        const compileObjectName = packageName ?? objectDetails.name;
        const compileObjectType = packageName ? 'package' : (objectDetails.type === 'stored-procedure' ? 'procedure' : 'function');
        void (async () => {
          try {
            const result = await window.electronAPI!.oracleDebugger.compileForDebug({
              connectionId: objectDetails.connectionId,
              schema: objectDetails.schema,
              objectName: compileObjectName,
              objectType: compileObjectType as 'procedure' | 'function' | 'package',
            });
            if (result.success) {
              toast.success({ title: 'Compiled with debug info' });
            } else {
              toast.error({ title: 'Compilation failed', description: result.error ?? 'Unknown error' });
            }
          } catch (error) {
            toast.error({ title: 'Error', description: error instanceof Error ? error.message : 'Compilation failed' });
          }
        })();
      }
      break;

    default:
      // Ignore unknown actions (submenu headers, etc.)
      break;
  }
}
